/** Local-time formatting helpers. Everything is shown in the device timezone. */

export function pad2(n: number): string {
  return Math.abs(Math.floor(n)).toString().padStart(2, '0');
}

/** "2026-09-18" for the device's local date (NOT toISOString, which is UTC). */
export function toLocalDateString(date: Date = new Date()): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** "07:30" for the device's local time. */
export function toLocalTimeString(date: Date = new Date()): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** "07:30:04" from an epoch-ms timestamp. */
export function formatClockTime(timestamp?: number): string {
  if (!timestamp) return '—';
  const d = new Date(timestamp);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

/**
 * Local ISO-8601 with offset, e.g. "2026-09-18T07:30:04+05:30".
 * The offset matters: the server must be able to reconstruct the local wall
 * clock the surveyor actually saw.
 */
export function toLocalIsoString(timestamp?: number): string | null {
  if (!timestamp) return null;
  const d = new Date(timestamp);
  const offsetMinutes = -d.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const offset = `${sign}${pad2(offsetMinutes / 60)}:${pad2(offsetMinutes % 60)}`;
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
    `T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}${offset}`
  );
}

/** "18 Sep 2026" */
export function formatDisplayDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${d} ${months[m - 1]} ${y}`;
}

/** 1284 -> "1,284" */
export function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * Turns an arbitrary field value into something safe for a filename on every
 * platform: ASCII word characters and dashes only.
 */
export function sanitizeFilenamePart(value: string, fallback = 'unknown'): string {
  const cleaned = value
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return cleaned.length > 0 ? cleaned : fallback;
}

/**
 * Combine an ISO date ("2026-09-18") and a "HH:MM" time into an epoch-ms
 * timestamp in the device's local timezone.
 */
export function localDateTimeToTimestamp(isoDate: string, time: string): number | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!dateMatch || !timeMatch) return null;
  const d = new Date(
    Number(dateMatch[1]),
    Number(dateMatch[2]) - 1,
    Number(dateMatch[3]),
    Number(timeMatch[1]),
    Number(timeMatch[2]),
    0,
    0,
  );
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}
